import { getUserRecommendations } from '@atlaskit/smart-user-picker';
import {
  AsyncSelect,
  FormatOptionLabelMeta,
  StylesConfig,
} from '@databases/atlaskit/Select';
import { useFlaggingCallback } from '@databases/banners';
import { getCloudId } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { KnownUser, UserReference } from '@databases/types/confluence';
import {
  renderUserMention,
  renderUserWithAvatar,
  AvatarWithLabel,
  UserName,
} from '@databases/user';
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl-next';

import { getIdentity } from '@canvas/connection/identity';

const getOptionValue = (value: KnownUser | UserReference) => {
  return value.accountId;
};

const styles: StylesConfig<any> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
  singleValue: (base) => ({
    ...base,
    lineHeight: '24px',
  }),
};

function isFullUser(value: KnownUser | UserReference): value is KnownUser {
  return 'type' in value;
}

interface UserPickerProps {
  value?: UserReference[];
  onChange: (value: UserReference[]) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isClearable?: boolean;
  isPending?: boolean;
  isMulti?: boolean;
  isDisabled?: boolean;
  isSeamless?: boolean;
  /** If true, render the selected value as a user mention rather than simple text. */
  isMentionPreview?: boolean;
}

const getUserRecommendationsParams = async (query: string) => {
  return {
    context: {
      contextType: 'contributors',
      principalId: (await getIdentity())?.accountId || '',
      productAttributes: {
        isEntitledConfluenceExternalCollaborator: false,
      },
      productKey: 'confluence',
      siteId: getCloudId() || '',
    },
    includeGroups: false,
    includeTeams: false,
    includeUsers: true,
    maxNumberOfResults: 15,
    performSearchQueryOnly: false,
    query,
  };
};

/**
 * An asynchronous select box that allows searching for users within the Confluence instance.
 */
const UserPicker: React.FunctionComponent<UserPickerProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isClearable,
  isPending,
  isMulti = false,
  isDisabled = false,
  isSeamless,
}) => {
  const intl = useIntl();
  const formatMessage = intl.formatMessage;

  const loadOptions = useFlaggingCallback(
    async (query: string): Promise<(KnownUser | UserReference)[]> => {
      const data = await getUserRecommendations(
        await getUserRecommendationsParams(query),
        intl
      );

      return data.map((user) => ({
        accountId: user.id,
        displayName: user.name,
        type: 'known',
        profilePicture: {
          path: user.avatarUrl,
        },
      }));
    },
    [intl]
  );

  const handleChange = useFlaggingCallback(
    (
      val: ReadonlyArray<KnownUser | UserReference> | KnownUser | UserReference | null
    ) => {
      const values = Array.isArray(val) ? val : val ? [val] : [];
      onChange(values.map(({ accountId, displayName }) => ({ accountId, displayName })));
    },
    [onChange]
  );

  const formatOptionLabel = useFlaggingCallback(
    (
      value: KnownUser | UserReference,
      meta: FormatOptionLabelMeta<KnownUser | UserReference>
    ): ReactNode => {
      if (meta.context === 'menu') {
        if (isFullUser(value)) {
          return <AvatarWithLabel userOrGroup={value} label={value.displayName} />;
        } else {
          return (
            <UserName
              accountId={value.accountId}
              displayName={value.displayName}
              renderUser={renderUserWithAvatar}
            />
          );
        }
      } else {
        return (
          <UserName
            accountId={value.accountId}
            displayName={value.displayName}
            renderUser={renderUserMention}
          />
        );
      }
    },
    []
  );

  return (
    <div style={{ width: '100%' }}>
      <AsyncSelect<KnownUser | UserReference, boolean>
        inputId="database-user-picker"
        value={value}
        onChange={handleChange}
        isClearable={isClearable}
        formatOptionLabel={formatOptionLabel}
        loadOptions={loadOptions}
        getOptionValue={getOptionValue}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        placeholder={formatMessage(messages['select-dropdown.placeholder'])}
        loadingMessage={() =>
          formatMessage(messages['field-settings.select-picker-loading-text'])
        }
        isLoading={isPending}
        styles={styles}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isSeamless={isSeamless}
        defaultMenuIsOpen={autoFocus && !value?.length}
      />
    </div>
  );
};

export default UserPicker;
